// @ts-strict-ignore
import { ItemUpdate } from 'lightstreamer-client-web';
import { LiveDataGroups, StorageKeys, Urls } from 'phoenix/constants';
import { LiveDataNamespaces } from 'phoenix/constants/LiveDataNamespaces';
import { Actions, LiveMessageOptions } from 'phoenix/redux/actions';
import { Order } from 'phoenix/redux/models';
import { SystemMessageDeliveryControls } from 'phoenix/redux/models/Messages/ClientMessageDeliveryControls';
import { SnexMessage } from 'phoenix/redux/models/Messages/SnexMessage';
import { SnexOrderUpdateMessagePayload } from 'phoenix/redux/models/Messages/SnexOrderUpdateMessagePayload';
import { UnreadMessagesStats } from 'phoenix/redux/models/Messages/UnreadMessagesStats';
import { SetPersistentStringAsync } from 'phoenix/resolvers/PersistentStringStorage';
import { SnexAxios } from 'phoenix/stores/AxiosHelpers';
import { GetJwtClaim, Jwt, Lightstreamer } from 'phoenix/util';
import { create } from 'zustand';
import { log, ZustandLiveSubscribe } from './helpers';
import { OrdersStore_MessageHandler } from './OrdersStore';
import { usePositionsStore } from './PositionsStore';

type MessagesStoreData = {
    statsLoading: boolean;
    stats: UnreadMessagesStats | null;
    messagesLoading: boolean;
    messageLoading: Record<string, boolean>;
    messages: SnexMessage[];
    latestMessage: SnexMessage | null;
    systemControls: SystemMessageDeliveryControls[]; // System-level MDC controls, as visible to the client
};

type MessagesStoreMethods = {
    recordLastViewedTime: (dt?: Date) => Promise<void>;
    clearUnreadStats: () => void;
    loadUnreadStats: () => Promise<UnreadMessagesStats | number | null>;
    incrementUnread: () => number;
    clearAll: () => void;
    addMessage: (message: SnexMessage) => void;
    addSystemControl: (control: SystemMessageDeliveryControls) => void;
    subscribeToLiveMessages: (userSub?: string, options?: LiveMessageOptions) => Promise<string>;
    loadMessages: (page?: number, pageSize?: number) => Promise<SnexMessage[]>;
    getMessage: (messageId: string) => Promise<SnexMessage>;
    generateMessage: (reset?: boolean) => Promise<void>;
    markAsRead: (messageId: string) => Promise<void>;
    deleteMessages: (messageIds: string[]) => Promise<void>;
    deleteAllMessages: () => Promise<void>;
    getSystemMessageControls: () => Promise<SystemMessageDeliveryControls[]>;
};

export type MessagesStoreState = MessagesStoreData & MessagesStoreMethods;

export const useMessagesStore = create<MessagesStoreState>((set, get) => ({
    statsLoading: false,
    stats: null,
    messages: [],
    latestMessage: null,
    messagesLoading: false,
    messageLoading: {},
    systemControls: [],

    // Records the last time the user viewed messages. This is used to determine the unread message count.
    recordLastViewedTime: async (dt?: Date) => {
        const d = dt || new Date();
        log(`[MessagesStore] Setting last viewed time: ${d.toISOString()}`);
        await SetPersistentStringAsync(StorageKeys.MessagesLastViewedTime, d.toISOString());
    },

    // Clears the unread message stats for the current user.
    clearUnreadStats: () => {
        log('[MessagesStore] Clearing unread stats');
        set({ stats: { unreadCount: 0 } });
    },

    // Loads the unread message stats for the current user. This is used to display the unread message count in the UI.
    loadUnreadStats: async () => {
        try {
            set({ statsLoading: true });

            // In addition, unread-stats does not update immediately upon a message being read on web.
                const statsResult = await SnexAxios.ApiGet<UnreadMessagesStats>(Urls.messages.countStats()).run();
                set({ stats: statsResult, statsLoading: false });
                return statsResult;
        } catch (e) {
            console.error(e);
            return null;
        }
    },

    // Increments the unread message count by 1 and returns the new count.
    incrementUnread: () => {
        const existing = get().stats;
        const newCount = (existing?.unreadCount || 0) + 1;
        set({ stats: { ...existing, unreadCount: newCount } });
        return newCount;
    },

    // Clears all messages and stats. Currently not used on web.
    clearAll: () =>
        set({
            statsLoading: false,
            stats: null,
        }),

    addMessage: (message: SnexMessage) => {
        set((s) => ({ messages: [message, ...s.messages], latestMessage: message }));
    },

    addSystemControl: (control: SystemMessageDeliveryControls) => {
        set((s) => ({ systemControls: [control, ...s.systemControls] }));
    },

    // Subscribes to live messages for the current user.
    subscribeToLiveMessages: async (userSub?: string, options?: LiveMessageOptions) => {
        const opts = {
            fetchPositionsOnOrderUpdates: true, // for back compat
            ...options
        };
        const subEffective = userSub || GetJwtClaim(Lightstreamer.GetCurrentUserId(), 'sub');
        return ZustandLiveSubscribe(
            LiveDataGroups.Messages,
            [`msg:${subEffective}`],
            Actions.Messages.LiveStream,
            // prettier-ignore
            ['id','recipVulcanId','senderId','senderName','title','message','subjectSymbol','subjectUserId','time','type','payloadType','disposition','appPermalink','externalLink','imageUrl','readDate','data'],
            async (item: ItemUpdate) => {
                try {
                    const message = SnexMessage.FromLiveData(item);
                    return await MessagesStore_HandleMessageUpdate(message, opts);
                } catch (e) {
                    console.log('[MessagesStore] MESSAGE UPDATE ERROR', e);
                }
            },
            {
                mode: 'DISTINCT',
                includeSnapshot: false,
                maxFrequencySeconds: 1,
                namespace: LiveDataNamespaces.Messages
            }
        );
    },

    // Loads the (default) first page of messages. Can be paginated by passing in a page number and page size.
    loadMessages: async (page?: number, pageSize?: number) => {
        set({ messagesLoading: true });
        const filters = { page: page || 1, size: pageSize || 20 };
        const results = await SnexAxios.ApiGet<SnexMessage[]>(Urls.messages.search(filters)).run();
        set({ messages: [...get().messages, ...results], messagesLoading: false });
        return results;
    },

    getMessage: async (messageId: string) => {
        const existing = get().messages.find((m) => m.id === messageId);
        if (existing) return existing;
        set((s) => ({ messageLoading: { ...s.messageLoading, [messageId]: true } }));
        const result = await SnexAxios.ApiGet<SnexMessage>(Urls.messages.getOne(messageId)).run();
        // Sort the messages by date, newest first
        const messages = [result, ...get().messages].sort((a, b) => new Date(b.sendDate).getTime() - new Date(a.sendDate).getTime());
        set({ messages, messageLoading: { ...get().messageLoading, [messageId]: false } });
        return result;
    },

    // Generates a stock StoneX One Message for testing purposes. This returns the "Welcome to StoneX" message.
    generateMessage: async (reset = false) => {
        await SnexAxios.ApiPost(Urls.messages.generate())
            .run()
            .then(() => {
                if (reset) {
                    set({ messages: [] });
                    useMessagesStore.getState().loadMessages();
                }
            });
    },

    // Marks a message as read.
    markAsRead: async (messageId: string) => {
        await SnexAxios.ApiPut(Urls.messages.markRead([messageId])).run();
        set({
            messages: get().messages.map((m) => {
                if (m.id === messageId) m.readDate = new Date().toISOString();
                return m;
            }),
            stats: { ...get().stats, unreadCount: get().stats.unreadCount - 1 }
        });
    },

    // Deletes messages by ID.
    deleteMessages: async (messageIds: string[]) => {
        await SnexAxios.ApiDelete(Urls.messages.delete(messageIds.flatMap((l) => l.split(',')))).run();
        set({ messages: get().messages.filter((m) => !messageIds.includes(m.id)) });
    },

    // Deletes all messages.
    deleteAllMessages: async () => {
        await SnexAxios.ApiDelete(Urls.messages.deleteAll()).run();
        set({ messages: [] });
    },

    // Gets the system-level message delivery controls.
    getSystemMessageControls: async () => {
        const result = await SnexAxios.ApiGet<SystemMessageDeliveryControls[]>(Urls.messages.systemControls()).run();

        const newControls = result.filter((c) => !get().systemControls.some((sc) => sc.messageType === c.messageType));
        set({ systemControls: [...get().systemControls, ...newControls] });
        set({ systemControls: result });
        return result;
    }
}));

export const MessageStore_LoadStats = () => useMessagesStore.getState().loadUnreadStats();
export const MessageStore_IncrementUnread = () => useMessagesStore.getState().incrementUnread();
export const MessageStore_AddSystemControl = (control: SystemMessageDeliveryControls) => useMessagesStore.getState().addSystemControl(control);

const MessageStore_SubscribeToLiveMessages = async (userSub?: string, options?: LiveMessageOptions) =>
    await useMessagesStore.getState().subscribeToLiveMessages(userSub, options);

export const MessagesStore_HandleMessageUpdate = async (message: SnexMessage, opts: LiveMessageOptions): Promise<SnexMessage> => {
    MessageStore_IncrementUnread();

    if (message.data?.type === 'OrderUpdate') {
        log('[MessagesStore] Handling order update message', message);
        OrdersStore_MessageHandler(message);
        const order = message.data as SnexOrderUpdateMessagePayload & Order;
        if (opts.fetchPositionsOnOrderUpdates && order?.orderStatus?.toLowerCase()?.includes('fill')) {
            setTimeout(() => usePositionsStore.getState().load(), 10000);
        }
    }

    if (message.type.toLowerCase() !== 'silent') useMessagesStore.getState().addMessage(message);

    return message;
};

export const ZustandSubscribeToLiveMessagesWithJwt = (jwt?: string, options?: LiveMessageOptions) => {
    // Okta tokens need to use `cdsid:<CDSID>`; Vulcan tokens just use the token's subject.
    const iss = Jwt.GetIssuer(jwt);
    const sub = Jwt.TryGetSubject(jwt);
    const cds = Jwt.TryGetClaim(jwt, 'cds_id');
    const userId = iss === 'okta' ? `cdsid:${cds}` : sub;
    return MessageStore_SubscribeToLiveMessages(userId, options);
};
